import type { AriaRole, FunctionComponent, ReactElement, ReactNode } from 'react';
import { Fragment } from 'react';
import type { VariableSizeListProps } from 'react-window';
import cx from 'classnames';

import { useVirtualScroll } from './useVirtualScroll';
import styles from './VirtualScroll.module.scss';

type PaginationProps = {
    hasNextPage?: boolean;
    loadMoreItems?(): Promise<unknown>;
    isNextPageLoading?: boolean;
};

type Props = {
    /**
     * The list of items to render.
     */
    items: ReactElement[];
    /**
     * A loader element to show when loading more items.
     */
    loader?: ReactNode;
    /**
     * If set the virtual scroll is the scrollable element, window otherwise
     */
    maxHeight?: number | CSSStyleDeclaration['height'];
    /**
     * Getter of an approx item size for first render in the direction being windowed.
     */
    getItemSize?: (index: number) => number;
    /**
     * The number of items (rows or columns) to render outside of the visible area. This property can be important for two reasons:
     *
     * - Overscanning by one row or column allows the tab key to focus on the next (not yet visible) item.
     * - Overscanning slightly can reduce or prevent a flash of empty space when a user first starts scrolling.
     *
     * Note that overscanning too much can negatively impact performance. By default, List overscans by two items.
     */
    overscanCount?: number;
    /**
     * Render prop function which enables passing children to parent elements
     */
    render?(children: ReactNode): ReactNode;
    /**
     * Css selector to apply styles for parent container element instead of the default one
     */
    parentChildSelector?: string;
    /**
     * Enables scroll restoration after coming back if the items did not change
     */
    shouldRestoreScroll?: boolean;
    /**
     * Smoothes out the scroll position restoration animation when returning to the list
     */
    smoothScrollRestoration?: boolean;
    /**
     * Invoked when saving offset, can be used to calculate custom offset position
     */
    onOffsetSave?(): number;
    role?: AriaRole;
} & PaginationProps &
    Omit<Partial<VariableSizeListProps>, 'outerElementType'> & {
        outerElementType?: FunctionComponent;
    };

const DEFAULT_ITEM_SIZE = 100;
const DEFAULT_OVERSCAN_COUNT = 4;
const defaultGetItemSize = () => DEFAULT_ITEM_SIZE;

/**
 * A component that implements virtual scrolling for efficient rendering of large lists.
 *
 * @component
 * @template T The type of items in the list.
 *
 * @param {Object} props - The component props.
 * @param {ReactElement[]} props.items - The list of items to render.
 * @param {ReactNode} [props.loader] - A loader element to show when loading more items.
 * @param {number|string} [props.maxHeight] - Maximum height of the scrollable area. If set, the component becomes the scrollable element.
 * @param {(index: number) => number} [props.getItemSize] - Function to get the size of an item at a given index.
 * @param {number} [props.overscanCount=4] - Number of items to render outside the visible area.
 * @param {(children: ReactNode) => ReactNode} [props.render] - Custom render function for wrapping children.
 * @param {string} [props.parentChildSelector] - CSS selector for applying styles to the parent container.
 * @param {boolean} [props.shouldRestoreScroll=false] - Whether to restore scroll position when returning to the list.
 * @param {boolean} [props.smoothScrollRestoration=false] - Smoothes out the scroll position restoration animation when returning to the list.
 * @param {() => number} [props.onOffsetSave] - Function called when saving scroll offset.
 * @param {boolean} [props.hasNextPage=false] - Whether there are more items to load.
 * @param {() => Promise<unknown>} [props.loadMoreItems] - Function to load more items.
 * @param {boolean} [props.isNextPageLoading=false] - Whether the next page of items is currently loading.
 *
 * @example
 * <VirtualScroll
 *   items={listItems}
 *   maxHeight={500}
 *   getItemSize={(index) => listItems[index].height}
 *   hasNextPage={true}
 *   loadMoreItems={() => fetchMoreItems()}
 *   loader={<LoadingSpinner />}
 * />
 *
 * @remarks
 * - Supports both fixed height and variable height items.
 * - Implements infinite loading when `hasNextPage` is true and `loadMoreItems` is provided.
 */
export const VirtualScroll = ({
    maxHeight,
    items,
    getItemSize = defaultGetItemSize,
    loadMoreItems = () => Promise.resolve(),
    hasNextPage = false,
    isNextPageLoading = false,
    overscanCount = DEFAULT_OVERSCAN_COUNT,
    loader = null,
    render = (children) => children,
    parentChildSelector,
    shouldRestoreScroll = false,
    smoothScrollRestoration = false,
    onOffsetSave,
    role = 'list',
}: Props) => {
    const { parentRef, spacerRef, virtualItems } = useVirtualScroll({
        getItemSize,
        height: maxHeight,
        hasNextPage,
        items,
        overscan: overscanCount,
        isNextPageLoading,
        loadMoreItems,
        spacerSelector: parentChildSelector,
        shouldRestoreScroll,
        smoothScrollRestoration,
        onOffsetSave,
    });

    return (
        <div
            className={cx({ [styles.container]: !!maxHeight })}
            ref={parentRef}
            data-cy="virtual-scroll"
            role={role}
        >
            <div
                ref={spacerRef as React.RefObject<HTMLDivElement>}
                className={styles.spacer}
                data-cy="virtual-scroll-spacer"
            >
                {render(
                    virtualItems.map((virtualItem) => {
                        const isLoaderRow = virtualItem.index > items.length - 1;

                        return (
                            <Fragment key={virtualItem.key}>
                                {!isLoaderRow && items[virtualItem.index]}
                                {isLoaderRow && loader}
                            </Fragment>
                        );
                    }),
                )}
            </div>
        </div>
    );
};

VirtualScroll.displayName = 'VirtualScroll';
